import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Pill, Plus, Clock, CheckCircle, X, Loader2, Calendar, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Frequency keys for translation
const FREQUENCIES = [
  { value: "once_daily", labelKey: "medications.onceDaily" },
  { value: "twice_daily", labelKey: "medications.twiceDaily" },
  { value: "three_times", labelKey: "medications.threeTimes" },
  { value: "four_times", labelKey: "medications.fourTimes" },
  { value: "as_needed", labelKey: "medications.asNeeded" },
  { value: "weekly", labelKey: "medications.weekly" },
];

export default function MedicationManagement() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [medications, setMedications] = useState([]);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newMed, setNewMed] = useState({
    name: "",
    dosage: "",
    frequency: "once_daily",
    times: ["08:00"],
    instructions: "",
    start_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (user) {
      loadMedications();
    }
  }, [user]);

  const loadMedications = async () => {
    setIsLoading(true);
    try {
      const [medsRes, scheduleRes] = await Promise.all([
        api.get("/medications"),
        api.get("/medications/today")
      ]);
      setMedications(medsRes.data.medications || []);
      setTodaySchedule(scheduleRes.data.schedule || []);
    } catch (error) {
      console.error("Failed to load medications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMedication = async () => {
    if (!newMed.name || !newMed.dosage) {
      toast.error("Please fill in medication name and dosage");
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post("/medications", newMed);
      toast.success("Medication added successfully");
      setShowAdd(false);
      setNewMed({
        name: "",
        dosage: "",
        frequency: "once_daily",
        times: ["08:00"],
        instructions: "",
        start_date: new Date().toISOString().split('T')[0]
      });
      loadMedications();
    } catch (error) {
      toast.error("Failed to add medication");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogMedication = async (medicationId, status = "taken") => {
    try {
      await api.post(`/medications/${medicationId}/log`, null, { params: { status } });
      toast.success(status === "taken" ? "Medication logged!" : "Marked as skipped");
      loadMedications();
    } catch (error) {
      toast.error("Failed to log medication");
    }
  };

  const handleDeleteMedication = async (medicationId) => {
    try {
      await api.delete(`/medications/${medicationId}`);
      toast.success("Medication removed");
      loadMedications();
    } catch (error) {
      toast.error("Failed to remove medication");
    }
  };

  const updateTimes = (count) => {
    const defaultTimes = ["08:00", "12:00", "18:00", "22:00"];
    setNewMed(prev => ({
      ...prev,
      times: defaultTimes.slice(0, count)
    }));
  };

  if (!user) {
    return (
      <div className="container-max px-6 py-12 text-center">
        <Pill className="w-16 h-16 mx-auto mb-4 text-primary/30" />
        <h1 className="text-2xl font-serif mb-2">{t('medications.title')}</h1>
        <p className="text-muted-foreground">{t('medications.signInToManage', 'Sign in to manage your medications')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container-max px-6 py-12 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container-max px-6 py-8" data-testid="medication-management">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">{t('medications.title')}</h1>
          <p className="text-muted-foreground">{t('medications.trackSchedule')}</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button data-testid="add-medication-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('medications.add')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('medications.addNew', 'Add New Medication')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('medications.name')}</Label>
                  <Input
                    placeholder={t('medications.namePlaceholder', 'e.g., Morphine')}
                    value={newMed.name}
                    onChange={(e) => setNewMed(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="med-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('medications.dosage')}</Label>
                  <Input
                    placeholder={t('medications.dosagePlaceholder', 'e.g., 10mg')}
                    value={newMed.dosage}
                    onChange={(e) => setNewMed(prev => ({ ...prev, dosage: e.target.value }))}
                    data-testid="med-dosage-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{t('medications.frequency')}</Label>
                <Select
                  value={newMed.frequency}
                  onValueChange={(val) => {
                    setNewMed(prev => ({ ...prev, frequency: val }));
                    if (val === "twice_daily") updateTimes(2);
                    else if (val === "three_times") updateTimes(3);
                    else if (val === "four_times") updateTimes(4);
                    else updateTimes(1);
                  }}
                >
                  <SelectTrigger data-testid="med-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{t(f.labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {newMed.frequency !== "as_needed" && (
                <div className="space-y-2">
                  <Label>{t('medications.times')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {newMed.times.map((time, i) => (
                      <Input
                        key={i}
                        type="time"
                        value={time}
                        onChange={(e) => {
                          const newTimes = [...newMed.times];
                          newTimes[i] = e.target.value;
                          setNewMed(prev => ({ ...prev, times: newTimes }));
                        }}
                        className="w-32"
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('medications.instructions')} ({t('common.optional')})</Label>
                <Textarea
                  placeholder={t('medications.instructionsPlaceholder', 'e.g., Take with food')}
                  value={newMed.instructions}
                  onChange={(e) => setNewMed(prev => ({ ...prev, instructions: e.target.value }))}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('medications.startDate', 'Start Date')}</Label>
                <Input
                  type="date"
                  value={newMed.start_date}
                  onChange={(e) => setNewMed(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>

              <Button 
                className="w-full" 
                onClick={handleAddMedication}
                disabled={isSubmitting}
                data-testid="save-medication-btn"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                {t('medications.add')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Today's Schedule */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              {t('medications.todaySchedule')}
            </CardTitle>
            <CardDescription>
              {todaySchedule.filter(s => s.taken).length} {t('medications.of', 'of')} {todaySchedule.length} {t('medications.completed', 'completed')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {todaySchedule.length > 0 ? (
              <div className="space-y-3">
                {todaySchedule.map((item, i) => (
                  <div 
                    key={i}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      item.taken ? 'bg-green-50 border-green-200' : 'bg-card'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        item.taken ? 'bg-green-100' : 'bg-primary/10'
                      }`}>
                        {item.taken ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <Clock className="w-6 h-6 text-primary" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{item.medication?.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.medication?.dosage} • {t('medications.scheduled', 'Scheduled')}: {item.scheduled_time}
                        </p>
                        {item.medication?.instructions && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {item.medication.instructions}
                          </p>
                        )}
                      </div>
                    </div>
                    {!item.taken && (
                      <div className="flex gap-2">
                        <Button 
                          size="sm"
                          onClick={() => handleLogMedication(item.medication?.id, "taken")}
                          data-testid={`take-med-${item.medication?.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          {t('medications.taken')}
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => handleLogMedication(item.medication?.id, "skipped")}
                        >
                          {t('medications.skip')}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Pill className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t('medications.noScheduled')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* All Medications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Pill className="w-5 h-5 text-primary" />
              {t('medications.allMedications')}
            </CardTitle>
            <CardDescription>{medications.length} {t('medications.activeMedications', 'active medications')}</CardDescription>
          </CardHeader>
          <CardContent>
            {medications.length > 0 ? (
              <div className="space-y-3">
                {medications.map((med) => (
                  <div 
                    key={med.id}
                    className="flex items-center justify-between p-4 rounded-lg border"
                  >
                    <div>
                      <p className="font-medium">{med.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {med.dosage} • {t(FREQUENCIES.find(f => f.value === med.frequency)?.labelKey || 'medications.onceDaily')}
                      </p>
                      {med.times?.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {med.times.map((time, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {time}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button 
                      size="icon"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDeleteMedication(med.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Pill className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t('medications.noAdded', 'No medications added yet')}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setShowAdd(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('medications.addFirst', 'Add Your First Medication')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
